import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpenCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PullToRefresh from "../components/PullToRefresh";

const FILTERS = [
  { key: "all",    label: "All",    unreadOnly: false },
  { key: "unread", label: "Unread", unreadOnly: true  },
];

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 14) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

function NotifBody({ n }) {
  if (n.type === "share") {
    return (
      <Link to={`/concept/${n.payload?.concept_id}`} className="block">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">
          {n.payload?.from_name || "Someone"} shared
        </div>
        <div className="serif text-xl text-foreground leading-snug">{n.payload?.title || "a correlation"}</div>
        {n.payload?.message && <p className="text-sm text-muted-foreground italic mt-1">"{n.payload.message}"</p>}
      </Link>
    );
  }
  if (n.type === "recommendation") {
    return (
      <Link to={`/concept/${n.payload?.concept_id}`} className="block">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] mb-1">Recommended for you</div>
        <div className="serif text-xl text-foreground">{n.payload?.title}</div>
        {n.payload?.reason && <p className="text-sm text-muted-foreground mt-1">{n.payload.reason}</p>}
      </Link>
    );
  }
  if (n.type === "submission_approved") {
    return (
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">Your submission was admitted</div>
        <div className="serif text-xl text-foreground">{n.payload?.title}</div>
      </div>
    );
  }
  if (n.type === "reply") {
    return (
      <Link to={`/concept/${n.payload?.concept_id}`} className="block">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">
          {n.payload?.from_name || "Someone"} replied
        </div>
        <div className="serif text-xl text-foreground">on {n.payload?.concept_title || "a correlation"}</div>
        {n.payload?.snippet && <p className="text-sm text-muted-foreground italic mt-1">"{n.payload.snippet}"</p>}
      </Link>
    );
  }
  if (n.type === "new_correlation") {
    const cid = n.payload?.correlation_id;
    const to = cid ? `/weekly?sort=recent#item-${cid}` : "/weekly?sort=recent";
    return (
      <Link to={to} className="block" data-testid="inbox-new-correlation-link">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">
          {n.payload?.from_name || n.payload?.from_handle || "A reader"} shared a new correlation
        </div>
        <div className="serif text-xl text-foreground leading-snug">{n.payload?.title || "Untitled correlation"}</div>
        {n.payload?.snippet && (
          <p className="serif italic text-muted-foreground mt-1 line-clamp-2">&ldquo;{n.payload.snippet}&rdquo;</p>
        )}
      </Link>
    );
  }
  if (n.type === "feedback") {
    return (
      <Link to="/admin?tab=feedback" className="block">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] mb-1">
          New {n.payload?.kind || "feedback"} from {n.payload?.from_name || "a reader"}
        </div>
        {n.payload?.snippet && <p className="text-sm text-muted-foreground italic">"{n.payload.snippet}"</p>}
      </Link>
    );
  }
  if (n.type === "follow") {
    const slug = n.payload?.from_handle || n.payload?.from_user_id;
    return (
      <Link to={slug ? `/u/${slug}` : "/inbox"} className="block">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">
          {n.payload?.from_name || "Someone"} followed you
        </div>
        {n.payload?.from_handle && (
          <p className="text-sm text-muted-foreground italic mt-1">@{n.payload.from_handle}</p>
        )}
      </Link>
    );
  }
  if (n.type === "dm") {
    const slug = n.payload?.from_handle || n.payload?.from_user_id;
    return (
      <Link to={slug ? `/messages/${slug}` : "/inbox"} className="block" data-testid="inbox-dm-link">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">
          {n.payload?.from_name || "A reader"} sent you a message
        </div>
        {n.payload?.from_handle && (
          <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 mb-1">@{n.payload.from_handle}</div>
        )}
        {n.payload?.snippet && (
          <p className="serif italic text-foreground/90 mt-1">&ldquo;{n.payload.snippet}&rdquo;</p>
        )}
      </Link>
    );
  }
  return <div className="serif text-foreground">{n.payload?.title || "Notification"}</div>;
}

export default function Inbox() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    setLoadingList(true);
    const opt = FILTERS.find((f) => f.key === filter);
    api.get("/notifications", { params: { unread_only: opt?.unreadOnly ? true : undefined, limit: 100 } })
      .then(({ data }) => {
        setItems(data.items || []);
        setUnread(data.unread || 0);
        setTotal(data.total || 0);
      })
      .finally(() => setLoadingList(false));
  }, [user, loading, filter, navigate]);

  const refreshNotifications = useCallback(async () => {
    const opt = FILTERS.find((f) => f.key === filter);
    try {
      const { data } = await api.get("/notifications", {
        params: { unread_only: opt?.unreadOnly ? true : undefined, limit: 100 },
      });
      setItems(data.items || []);
      setUnread(data.unread || 0);
      setTotal(data.total || 0);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[inbox] soft-fail:", e?.message); }
  }, [filter]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((cur) => cur.map((n) => ({ ...n, read: true })));
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[inbox] soft-fail:", e?.message); }
  };

  if (!user) return null;

  return (
    <PullToRefresh onRefresh={refreshNotifications}>
    <div data-testid="inbox-page" className="max-w-4xl mx-auto px-6 sm:px-10 py-20">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-2">
        <div>
          <div className="label mb-2 text-[hsl(var(--accent))]">Inbox</div>
          <h1 className="serif text-5xl text-foreground leading-none">Messages from the archive</h1>
        </div>
        <span className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {total} total · {unread} unread
        </span>
      </div>

      <div className="mt-12 flex items-center justify-between hairline-b pb-4">
        <div className="flex gap-6">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`inbox-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`mono text-[11px] uppercase tracking-[0.22em] link-underline ${filter === f.key ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {unread > 0 && (
          <button
            data-testid="inbox-mark-all"
            onClick={markAllRead}
            className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
          >
            Mark all read
          </button>
        )}
      </div>

      {loadingList ? (
        <div className="py-20 text-center serif italic text-muted-foreground text-lg">Unsealing the post…</div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center">
          <BookOpenCheck size={28} className="mx-auto text-muted-foreground/60 mb-4" strokeWidth={1.5} />
          <p className="serif italic text-muted-foreground text-lg">
            {filter === "unread" ? "Nothing unread. You're up to date." : "Nothing here yet — pin, share, or react to start the conversation."}
          </p>
        </div>
      ) : (
        <ul className="mt-2">
          {items.map((n) => (
            <li
              key={n.id}
              data-testid={`inbox-item-${n.id}`}
              className={`hairline-b py-6 ${!n.read ? "pl-4 -ml-4 border-l-2 border-l-[hsl(var(--primary))]" : ""}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <NotifBody n={n} />
                  <div className="mt-2 mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
                    {fmt(n.created_at)}
                  </div>
                </div>
                {!n.read && (
                  <span className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">New</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
    </PullToRefresh>
  );
}
